<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { isNavbarItemActive, type NavbarItem } from "@/components/shared/navbar/useNavbarItems.ts";

const props = defineProps<{
  items: Array<NavbarItem>;
  header?: string;
}>();

const route = useRoute();

const visibleItems = computed(() => props.items.filter((item) => !item.hidden));

const visibleChildren = (item: NavbarItem) =>
  (item.children ?? []).filter((child) => !child.hidden);

const groupKey = (index: number) => `nav-group-${index}`;

const isActive = (entry: NavbarItem) => !!entry.url && isNavbarItemActive(entry, route.path);

const isParentActive = (item: NavbarItem) =>
  isActive(item) || visibleChildren(item).some((child) => isActive(child));

const opened = ref<string[]>(
  visibleItems.value
    .map((item, index) => (isParentActive(item) ? groupKey(index) : undefined))
    .filter((key): key is string => key !== undefined)
);

const flowColorStyle = (item: NavbarItem) => ({
  "--flow-color": item.accentColor ?? "rgb(var(--v-theme-secondary))",
});

const badgeColorStyle = (item: NavbarItem) => ({
  "--badge-color": item.badgeColor ?? "rgb(var(--v-theme-secondary))",
});
</script>

<template>
  <v-list
    v-if="visibleItems.length > 0"
    v-model:opened="opened"
  >
    <v-list-subheader v-if="header">
      {{ header }}
    </v-list-subheader>
    <template
      v-for="(item, index) in visibleItems"
      :key="index"
    >
      <v-list-group
        v-if="visibleChildren(item).length > 0"
        :value="groupKey(index)"
      >
        <template #activator="{ props: activatorProps, isOpen }">
          <v-list-item
            v-bind="activatorProps"
            class="nav-item"
            :class="{ 'nav-item--active': isParentActive(item) && !isOpen }"
            :disabled="item.disabled"
            :ripple="false"
          >
            <template #prepend>
              <v-icon :icon="item.icon" />
            </template>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </template>

        <v-list-item
          v-for="(child, childIndex) in visibleChildren(item)"
          :key="`${index}-${childIndex}`"
          class="tw:pl-12! nav-child-item"
          :class="{
            'nav-child-item--active': isActive(child),
          }"
          :disabled="child.disabled"
          :ripple="false"
          :style="flowColorStyle(child)"
          :to="child.onClick ? undefined : (child.to ?? child.url)"
          @click="child.onClick?.()"
        >
          <template
            v-if="child.icon"
            #prepend
          >
            <v-icon :icon="child.icon" />
          </template>
          <v-list-item-title class="nav-child-item__title">
            {{ child.title }}
          </v-list-item-title>
          <template
            v-if="child.badgeNumber && child.badgeNumber > 0"
            #append
          >
            <v-badge
              inline
              :content="child.badgeNumber > 99 ? '99+' : child.badgeNumber"
              :max="99"
              class="nav-child-badge"
              :style="badgeColorStyle(child)"
            />
          </template>
        </v-list-item>
      </v-list-group>

      <v-list-item
        v-else
        class="nav-item"
        :class="{ 'nav-item--active': isActive(item) }"
        :disabled="item.disabled"
        :ripple="false"
        :to="item.onClick ? undefined : (item.to ?? item.url)"
        @click="item.onClick?.()"
      >
        <template #prepend>
          <v-icon :icon="item.icon" />
        </template>
        <v-list-item-title>{{ item.title }}</v-list-item-title>
      </v-list-item>
    </template>
  </v-list>
</template>

<style scoped>
.nav-item :deep(.v-list-item__overlay),
.nav-child-item :deep(.v-list-item__overlay) {
  display: none;
}

.nav-item {
  margin-inline: 8px;
  border-radius: 8px;
  color: rgba(var(--v-theme-on-surface), 0.65);
  transition:
    color 0.16s ease,
    background-color 0.16s ease;
}

.nav-item:hover {
  color: rgb(var(--v-theme-on-surface));
  background-color: rgba(var(--v-theme-on-surface), 0.055);
}

.nav-item--active {
  color: rgb(var(--v-theme-on-surface));
  background-color: rgba(var(--v-theme-secondary), 0.09);
}

.nav-item--active :deep(.v-list-item-title) {
  font-weight: 700;
}

.nav-child-item {
  position: relative;
  margin-inline: 4px;
  border-radius: 8px;
  min-height: 44px;
  color: rgba(var(--v-theme-on-surface), 0.65);
  /* Vuetify reserves 32px either side of the icon/badge by default - it was
     starving the title of the width it needs to wrap instead of breaking mid-word. */
  --v-list-prepend-gap: 8px;
  transition:
    color 0.16s ease,
    background-color 0.16s ease;
}

.nav-child-item:hover {
  color: rgb(var(--v-theme-on-surface));
  background-color: color-mix(in srgb, var(--flow-color) 8%, transparent);
}

.nav-child-item--active {
  color: rgb(var(--v-theme-on-surface));
  background: color-mix(in srgb, var(--flow-color) 10%, transparent);
}

.nav-child-item--active :deep(.v-list-item-title) {
  font-weight: 700;
}

.nav-child-item--active::before {
  content: "";
  position: absolute;
  inset-block: 8px;
  inset-inline-start: 0;
  width: 2px;
  border-radius: 2px;
  background: var(--flow-color);
}

.nav-child-item--divider {
  margin-top: 8px;
  padding-top: 8px !important;
  border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.nav-child-item__title {
  white-space: normal !important;
  line-height: 1.25;
}

.nav-child-badge {
  flex-shrink: 0;
}

.nav-child-badge :deep(.v-badge__badge) {
  min-width: 20px;
  height: 18px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--badge-color) !important;
  color: #fff !important;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
}
</style>
